import { chains } from "@gearbox-protocol/sdk";
import {
  Button,
  CopyButton,
  ExpandableCard,
  formatTimestamp,
} from "@gearbox-protocol/ui-kit";
import { ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";
import { ProposalSignatures } from "@/components/proposals/proposal-signatures";
import { RecoveryMessageInfo } from "@/core/recovery";
import { useChainName } from "@/hooks/use-chain-list";
import { useExecuteRecovery } from "@/hooks/use-execute-recovery";
import { useSignRecovery } from "@/hooks/use-sign-recovery";

interface RecoveryCardProps {
  recovery: RecoveryMessageInfo;
}

export function RecoveryCard({ recovery }: RecoveryCardProps) {
  const [recoveryMessage, setRecoveryMessage] =
    useState<RecoveryMessageInfo>(recovery);

  const {
    sign,
    canSign,
    threshold,
    isPending: isSigning,
  } = useSignRecovery({
    recovery,
    updateRecoveries: setRecoveryMessage,
  });

  const { execute, isPending: isExecuting } = useExecuteRecovery({
    recovery,
    updateRecoveries: setRecoveryMessage,
  });

  const chainName = useChainName(recoveryMessage.chainId);
  const explorerUrl = Object.values(chains).find(
    c => c.id === recoveryMessage.chainId,
  )?.blockExplorers?.default?.url;

  const SignButton = useMemo(() => {
    if (recoveryMessage.transactionHash) {
      return (
        <div className="flex items-center gap-4">
          <span className="text-muted-foreground">
            {recoveryMessage.timestamp
              ? formatTimestamp(recoveryMessage.timestamp)
              : "N/A"}
          </span>
          <span className="flex items-center text-foreground gap-1">
            <span className="text-2xl">•</span>
            <span>Executed</span>
          </span>
          {recoveryMessage.transactionHash && explorerUrl && (
            <div className="mx-[-12px]">
              <Button
                variant="ghost"
                size="icon"
                onClick={e => {
                  e.stopPropagation();
                  window.open(
                    `${explorerUrl}${explorerUrl.endsWith("/") ? "" : "/"}tx/${
                      recoveryMessage.transactionHash
                    }`,
                    "_blank",
                  );
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (recoveryMessage.signatures.length < threshold) {
      return (
        <Button
          variant="outline"
          onClick={async e => {
            e.stopPropagation();
            await sign();
          }}
          disabled={!canSign || isSigning}
          className="px-6 bg-transparent border border-success text-success hover:bg-success/10"
        >
          {isSigning ? "Signing..." : "Sign"}
        </Button>
      );
    }

    return (
      <Button
        variant="outline"
        className="px-6 bg-transparent border border-success text-success hover:bg-success/10"
        onClick={async e => {
          e.stopPropagation();
          await execute();
        }}
        disabled={isExecuting}
      >
        {isExecuting ? "Executing..." : "Execute"}
      </Button>
    );
  }, [
    recoveryMessage,
    threshold,
    isSigning,
    isExecuting,
    explorerUrl,
    canSign,
    sign,
    execute,
  ]);

  return (
    <ExpandableCard
      header={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-muted-foreground">#{recoveryMessage.id}</span>
            <div className="flex items-center">
              <span className="text-foreground">
                {recoveryMessage.chainName} Recovery Message
              </span>
            </div>
          </div>
          {SignButton}
        </div>
      }
    >
      <div className="grid grid-cols-[1fr_minmax(420px,max-content)] gap-12 overflow-x-auto">
        <div className="space-y-2">
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-muted-foreground">Chain Id:</span>
            <div className="flex items-center gap-2 font-mono text-sm">
              <span>{recoveryMessage.chainId}</span>
              <span className="text-muted-foreground">[{chainName}]</span>
            </div>
          </div>

          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-muted-foreground">Starting Batch Hash:</span>
            <div className="flex items-center gap-1 font-mono text-sm">
              {recoveryMessage.startingBatchHash}
              <CopyButton
                text={recoveryMessage.startingBatchHash}
                name="Hash"
              />
            </div>
          </div>
        </div>

        <div className="border-l border-input pl-8">
          <ProposalSignatures
            signers={recoveryMessage.signatures.map(t => t.signer) || []}
            isExecuted={recoveryMessage.transactionHash !== undefined}
          />
        </div>
      </div>
    </ExpandableCard>
  );
}
